import React, { ComponentProps, ReactNode, useCallback } from 'react'
import { ControllerProps, useController } from 'react-hook-form'

import { bemJoin } from 'bem-join'
import classnames from 'classnames'

import { FormField } from '@app/lib/display/FormField/FormField'
import { MergeProps } from '@app/lib/types'

import './ChoiceField.scss'

type RegisterChoiceFn<V> = (args: { value: V }) => {
  onChange: (checked: boolean) => void
  onBlur: () => void
  name: string
  checked: boolean
  value: V
}

export type ToggleFieldProps<V> = MergeProps<
  ControllerProps,
  {
    label: ReactNode | ((props: ComponentProps<'label'>) => ReactNode)
    description?: ReactNode
    loading?: boolean
    render: (args: { register: RegisterChoiceFn<V> }) => ReactNode
    className?: string
    compare?: (a: V, b: V) => boolean
  }
>

const b = bemJoin('choice-field')

export const ChoiceField = <V,>({
  label,
  description,
  render,
  loading,
  className,
  compare = (a, b) => a === b,
  name,
  ...controllerProps
}: ToggleFieldProps<V>) => {
  const {
    field,
    fieldState: { error }
  } = useController({ name, ...controllerProps })

  const register: RegisterChoiceFn<V> = useCallback(
    ({ value }) => {
      return {
        name,
        value,
        checked: field.value && compare(field.value, value),
        onChange: (checked) => {
          field.onChange(checked ? value : undefined)
        },
        onBlur: () => field.onBlur(),
        className: b('input', {
          invalid: !!error,
          loading: loading
        })
      }
    },
    [field.onChange, field.value]
  )

  const Label = (labelProps: ComponentProps<any>) => {
    if (typeof label === 'function') {
      return label(labelProps)
    }

    return <label {...labelProps}>{label}</label>
  }

  return (
    <FormField
      label={!!label}
      description={!!description}
      error={!!error}
      className={classnames(
        b({
          invalid: !!error,
          loading: loading
        }),
        className
      )}
      render={({ fieldProps, labelProps, inputProps, descriptionProps, errorMessageProps }) => (
        <div {...fieldProps}>
          <Label {...labelProps} className={b('label')} />
          <fieldset {...inputProps}>
            {render({
              register
            })}
          </fieldset>
          {description && (
            <div {...descriptionProps} className={b('description')}>
              {description}
            </div>
          )}
          {error && (
            <div {...errorMessageProps} className={b('error')}>
              {error.message}
            </div>
          )}
        </div>
      )}
    />
  )
}
